import {Dispatch, SetStateAction, useCallback, useRef, useSyncExternalStore} from 'react';

type SetValue<T> = Dispatch<SetStateAction<T>>;

const CHANGE_EVENT = 'local-storage';

const subscribe = (callback: () => void) => {
	window.addEventListener('storage', callback);
	window.addEventListener(CHANGE_EVENT, callback);
	return () => {
		window.removeEventListener('storage', callback);
		window.removeEventListener(CHANGE_EVENT, callback);
	};
};

const readRaw = (key: string) => {
	try {
		return window.localStorage.getItem(key);
	} catch {
		return null;
	}
};

/**
 * Значение из localStorage.
 *
 * При гидрации отдаёт initialValue, как и сервер, и сразу после неё — сохранённое значение.
 * Компонент, смонтированный позже (например, при переходе на другую страницу), получает
 * сохранённое значение уже в первом рендере.
 */
export function useLocalStorage<T>(key: string, initialValue: T): [T, SetValue<T>] {
	const cache = useRef<{key: string; raw: string | null; value: T} | null>(null);

	const getSnapshot = useCallback(() => {
		const raw = readRaw(key);
		if (!cache.current || cache.current.key !== key || cache.current.raw !== raw) {
			const parsed = raw === null ? undefined : parseJSON<T>(raw);
			cache.current = {key, raw, value: parsed === undefined ? initialValue : parsed};
		}
		return cache.current.value;
	}, [key]);

	const getServerSnapshot = useCallback(() => initialValue, [initialValue]);

	const value = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

	const setValue: SetValue<T> = useCallback(
		(next) => {
			try {
				const resolved = next instanceof Function ? next(getSnapshot()) : next;
				window.localStorage.setItem(key, JSON.stringify(resolved));
				window.dispatchEvent(new Event(CHANGE_EVENT));
			} catch (error) {
				console.warn(`Error setting localStorage key “${key}”:`, error);
			}
		},
		[key, getSnapshot],
	);

	return [value, setValue];
}

function parseJSON<T>(value: string): T | undefined {
	try {
		return value === 'undefined' ? undefined : JSON.parse(value);
	} catch {
		return undefined;
	}
}
